// src/cn/pages/TrainingFollowup/TrainingFollowupPageCN.jsx
import { useCallback, useMemo, useState } from "react";
import { TrainingFollowupAI } from "../../../lib/trainingFollowupApi";
import { buildReportHtml } from "../../../lib/reportRenderer";

/* 공통 복사 버튼 */
function CopyButton({ text, className = "", label = "复制" }) {
  const onCopy = useCallback(async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      alert("已复制到剪贴板。");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      alert("已复制到剪贴板。");
    }
  }, [text]);

  return (
    <button type="button" onClick={onCopy} className={className}>
      {label}
    </button>
  );
}

/* 간단 이스케이프 (오류문구 등 안전 출력용) */
function escapeHtml(s = "") {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export default function TrainingFollowupPageCN() {
  // 1) 후속 메시지
  const [concernType, setConcernType] = useState("经济障碍");
  const [patientNotes, setPatientNotes] = useState("");
  const [messageDraft, setMessageDraft] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState("");

  // 2) 아웃리치 스크립트
  const [outreachTheme, setOutreachTheme] = useState("夏季减脂准备");
  const [targetProfile, setTargetProfile] = useState("");
  const [scriptDraft, setScriptDraft] = useState("");
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptError, setScriptError] = useState("");

  // 3) 맞춤 플랜
  const [planFocus, setPlanFocus] = useState("重点减重");
  const [planProfile, setPlanProfile] = useState("");
  const [planDraft, setPlanDraft] = useState("");
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState("");

  const busy = useMemo(() => msgLoading || scriptLoading || planLoading, [msgLoading, scriptLoading, planLoading]);

  // ===== 생성 핸들러들 =====
  const onGenerateMessage = useCallback(async () => {
    setMsgLoading(true);
    setMsgError("");
    setMessageDraft("");
    try {
      const res = await TrainingFollowupAI.message({
        concernType,
        patientNotes: patientNotes?.trim() || "",
      });
      setMessageDraft((res?.answer || "").trim());
    } catch (e) {
      setMsgError(e?.message || "生成失败");
    } finally {
      setMsgLoading(false);
    }
  }, [concernType, patientNotes]);

  const onGenerateScript = useCallback(async () => {
    setScriptLoading(true);
    setScriptError("");
    setScriptDraft("");
    try {
      const res = await TrainingFollowupAI.outreach({
        theme: outreachTheme,
        profile: targetProfile?.trim() || "",
      });
      setScriptDraft((res?.answer || "").trim());
    } catch (e) {
      setScriptError(e?.message || "生成失败");
    } finally {
      setScriptLoading(false);
    }
  }, [outreachTheme, targetProfile]);

  const onGeneratePlan = useCallback(async () => {
    if (!planProfile.trim()) {
      alert("请输入患者画像与目标摘要。");
      return;
    }
    setPlanLoading(true);
    setPlanError("");
    setPlanDraft("");
    try {
      const res = await TrainingFollowupAI.plan({
        focus: planFocus,
        profile: planProfile.trim(),
      });
      setPlanDraft((res?.answer || "").trim());
    } catch (e) {
      setPlanError(e?.message || "生成失败");
    } finally {
      setPlanLoading(false);
    }
  }, [planFocus, planProfile]);

  return (
    <section className="trainingFollow">
      <div id="content_title">
        <h2>AI MaC 咨询（后续跟进）</h2>
        <p>提升咨询工作效率的 AI 伙伴</p>
      </div>

      <div className="content">
        {/* ===== 1) AI 환자 응대 어시스턴트 ===== */}
        <div className="section borderType">
          <h3 className="sectionTitle">AI 患者应对助手</h3>

          <div>
            <label className="inputTitle">1. 选择患者的核心顾虑</label>
            <select className=" useform" value={concernType} onChange={(e) => setConcernType(e.target.value)}>
              <option value="经济障碍">经济障碍（费用问题）</option>
              <option value="需要家庭商议">需要再考虑/与家人商议</option>
              <option value="不信任远程诊疗">对远程诊疗的不信任</option>
              <option value="过去负面经历">以往减肥失败经历</option>
            </select>
          </div>

          <div>
            <label className="inputTitle">2. 患者补充信息（可选）</label>
            <textarea
              rows={8}
              className=" useform"
              placeholder="例如：表示3个月项目费用比预期高。因照顾孩子运动时间不足。"
              value={patientNotes}
              onChange={(e) => setPatientNotes(e.target.value)}
            />
          </div>

          <div>
            <button className="submitbtn" onClick={onGenerateMessage} disabled={msgLoading || busy}>
              {msgLoading ? "生成中.." : "生成定制后续信息"}
            </button>
          </div>

          {(messageDraft || msgError) && (
            <div className="trf-box">
              <div className="trf-box-head">AI 生成信息草稿</div>
              <div className="trf-box-body">
                <textarea rows={10} readOnly value={msgError ? `错误: ${msgError}` : messageDraft || ""} />
                <div>
                  <CopyButton text={messageDraft} className="copy" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ===== 2) AI 주도적 환자 관리 도우미 ===== */}
        <div className="section borderType">
          <h3 className="sectionTitle">AI 主动患者管理助手</h3>

          <div>
            <label className="inputTitle">1. 选择咨询主题</label>
            <select className=" useform" value={outreachTheme} onChange={(e) => setOutreachTheme(e.target.value)}>
              <option value="夏季减脂准备">夏季减脂准备</option>
              <option value="节后排毒">节后排毒与体重管理</option>
              <option value="新年健康目标达成">新年健康目标达成</option>
              <option value="2周体验项目介绍">2周体验项目特别介绍</option>
              <option value="健康提升与活力恢复">整体健康提升与活力恢复</option>
            </select>
          </div>

          <div>
            <label className="inputTitle">2. 目标患者画像（可选）</label>
            <textarea
              rows={4}
              className=" useform"
              placeholder="例如：活动量少的上班族、产后想减重的主妇、有过减肥失败经历的人"
              value={targetProfile}
              onChange={(e) => setTargetProfile(e.target.value)}
            />
          </div>

          <div>
            <button className="submitbtn" onClick={onGenerateScript} disabled={scriptLoading || busy}>
              {scriptLoading ? "生成中.." : "生成 AI 咨询脚本"}
            </button>
          </div>

          {(scriptDraft || scriptError) && (
            <div className="trf-box ">
              <div className="trf-box-head">AI 生成脚本</div>
              <div className="trf-box-body">
                <textarea rows={10} readOnly value={scriptError ? `错误: ${scriptError}` : scriptDraft || ""} />
                <div>
                  <CopyButton text={scriptDraft} className="copy" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ===== 3) AI 맞춤 건강 플랜 제안 ===== */}
        <div className="section borderType">
          <h3 className="sectionTitle">AI 定制健康方案建议</h3>

          <div>
            <label className="inputTitle">1. 选择方案核心目标</label>
            <select className=" useform" value={planFocus} onChange={(e) => setPlanFocus(e.target.value)}>
              <option value="重点减重">重点减重</option>
              <option value="提升活力与恢复能量">提升活力与恢复能量</option>
              <option value="排毒与体质改善">排毒与体质改善</option>
              <option value="压力管理与身心稳定">压力管理与身心稳定</option>
            </select>
          </div>

          <div>
            <label className="inputTitle">2. 患者画像与目标摘要</label>
            <textarea
              rows={8}
              className=" useform"
              placeholder="例如：40岁出头女性，办公室工作，常加班与晚间聚餐压力大。近1年体重增加8kg。希望3个月减重10kg并改善慢性疲劳。"
              value={planProfile}
              onChange={(e) => setPlanProfile(e.target.value)}
            />
          </div>

          <div>
            <button className="submitbtn" onClick={onGeneratePlan} disabled={planLoading || busy}>
              {planLoading ? "生成中.." : "生成 AI 定制方案"}
            </button>
          </div>

          {(planDraft || planError) && (
            <div className="report">
              <div className="report-head">AI 生成定制健康方案（草案）</div>
              <div className="report-body">
                <div className="report-body-area">
                  {planError ? (
                    <div
                      className="report-markdown"
                      dangerouslySetInnerHTML={{
                        __html: `<p class="report-paragraph">错误: ${escapeHtml(planError)}</p>`,
                      }}
                    />
                  ) : (
                    <div
                      className="report-markdown"
                      dangerouslySetInnerHTML={{
                        __html: buildReportHtml(planDraft, {
                          iconHTML: "",
                        }),
                      }}
                    />
                  )}
                </div>
                <div className="report-actions">
                  <CopyButton text={planDraft} className="copy" label="复制方案内容" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
